import type { TrialMembership } from "@/stores/support/trials";

export type LeadFilter = "leads" | "trials" | "converted" | "suppressed" | "all";

export const leadFilters: LeadFilter[] = [
  "leads",
  "trials",
  "converted",
  "suppressed",
  "all",
];

/** Fresh visits (no query param) land here. A URL `filter` still wins. */
export const DEFAULT_LEAD_FILTER: LeadFilter = "all";

export const leadFilterFromQuery = (value: unknown): LeadFilter =>
  typeof value === "string" && leadFilters.includes(value as LeadFilter)
    ? (value as LeadFilter)
    : DEFAULT_LEAD_FILTER;

/** Omit the param for All so a fresh visit and the default stay the same URL. */
export const leadFilterQueryParam = (filter: LeadFilter): string | undefined =>
  filter === DEFAULT_LEAD_FILTER ? undefined : filter;

type LeadStatusValue = TrialMembership["status"] | "fulfilled";

/** Support is not a parent site: status labels are hardcoded English. */
export const leadStatusLabels: Record<LeadStatusValue, string> = {
  requested: "Requested",
  pending: "Pending",
  fullfilled: "Fulfilled",
  fulfilled: "Fulfilled",
  expired: "Expired",
  cancelled: "Cancelled",
  rejected: "Rejected",
  converted: "Converted",
};

const titleCaseStatus = (value: string) =>
  value
    .split(/[\s_-]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");

export const leadStatusLabel = (status: string): string =>
  leadStatusLabels[status as LeadStatusValue] ?? (titleCaseStatus(status) || status);

export const isSuppressed = (lead: Pick<TrialMembership, "status">) =>
  lead.status === "rejected" || lead.status === "cancelled";

export const isConverted = (lead: Pick<TrialMembership, "status">) =>
  lead.status === "converted";

/**
 * Trial date chip is clickable when the trial can still be provisioned.
 * Fulfilled / converted rows must not re-run activation.
 */
export const canInitializeTrial = (
  lead: Pick<TrialMembership, "status" | "trial_date" | "is_fulfilled">
): boolean => {
  if (!lead.trial_date) return false;
  if (lead.is_fulfilled === 1) return false;
  if (lead.status === "fulfilled" || lead.status === "fullfilled") return false;
  if (lead.status === "converted") return false;
  return true;
};

/** Same rule the old Trials page used: a dated trial, or any row that is not a pure lead/demo. */
export const isTrialListingRow = (
  row: Pick<TrialMembership, "trial_date" | "request_type">
) => {
  if (row.trial_date) return true;
  if (row.request_type === "lead" || row.request_type === "demo") return false;
  return true;
};

export const matchesLeadFilter = (
  lead: Pick<TrialMembership, "status" | "trial_date" | "request_type">,
  filter: LeadFilter
): boolean => {
  if (filter === "all") return true;
  if (filter === "converted") return isConverted(lead);
  if (filter === "suppressed") return isSuppressed(lead);
  if (filter === "trials") return isTrialListingRow(lead);
  // Open prospects only. Trial rows live under the Trials filter.
  return !isSuppressed(lead) && !isConverted(lead) && !isTrialListingRow(lead);
};
